import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CompositionSector, formatCompositionAmount, formatCompositionNumber, prepareComposition } from '../utils/compositionChart';

interface PdfCompositionChartProps {
  sectors: CompositionSector[];
  colors: string[];
  emptyStateText?: string;
}

const PdfCompositionChart = ({ sectors, colors, emptyStateText }: PdfCompositionChartProps) => {
  const { t } = useTranslation();
  const composition = useMemo(() => prepareComposition(sectors, colors), [sectors, colors]);
  const emptyText = emptyStateText ?? t('one_pager_report.empty_portfolio_state');
  const { sectors: prepared, total, scale } = composition;

  if (total <= 0) return <div className="composition-wallet-pdf"><div className="pdf-empty-state">{emptyText}</div></div>;

  return (
    <div className="composition-wallet-pdf pdf-composition-layout">
      <div className="pdf-portfolio-progress" role="img" aria-label={t('one_pager_report.composition_by_sector')}>
        {prepared.map(sector => (
          <div key={sector.region} className="pdf-portfolio-progress-segment"
            style={{ width: `${sector.percent}%`, background: sector.color }}
            title={`${sector.region}: ${formatCompositionAmount(sector.percent)}%`} />
        ))}
      </div>
      <div className="pdf-portfolio-legend">
        {prepared.map(sector => (
          <span key={sector.region}><i style={{ background: sector.color }} />{sector.region} - {formatCompositionAmount(sector.percent)}%</span>
        ))}
      </div>
      <div className="pdf-chart-header">
        <span className="pdf-column-sector">{t('one_pager_report.composition_sector')}</span>
        <span className="pdf-column-division">{t('one_pager_report.composition_division')}</span>
        <div className="pdf-axis">
          <div className="pdf-axis-scale">
            <span className="pdf-axis-title">{t('one_pager_report.composition_approved_amount')}</span>
            <div className="pdf-axis-ticks">
              {scale.ticks.map((tick, index) => (
                <span key={tick}
                  className={`pdf-axis-tick${index === 0 ? ' pdf-axis-tick-start' : ''}${index === scale.ticks.length - 1 ? ' pdf-axis-tick-end' : ''}`}
                  style={{ left: `${tick / scale.max * 100}%` }}>
                  {formatCompositionNumber(tick, 0)}
                </span>
              ))}
            </div>
          </div>
          <span className="pdf-axis-value-space" aria-hidden="true" />
        </div>
        <span className="pdf-column-count">{t('one_pager_report.composition_project_count')}</span>
      </div>
      <div className="pdf-sector-list">
        {prepared.map(sector => sector.total <= 0 ? (
          <div key={sector.region} className="pdf-empty-state">{emptyText}</div>
        ) : (
          <div key={sector.region} className="pdf-bar-chart">
            <div className="pdf-sector-header" style={{ color: sector.color }}>
              <strong>{sector.region}</strong><p>{formatCompositionAmount(sector.total)} MM</p>
            </div>
            <div className="pdf-division-list">
              {sector.divisions.map(division => {
                const width = division.amount / scale.max * 100;
                return (
                  <div key={division.label} className="pdf-division-row">
                    <span className="pdf-division-label">{division.label}</span>
                    <div className="pdf-division-plot">
                      <div className="pdf-division-bar-area">
                        <div className="pdf-division-track"><div className="pdf-division-fill" style={{ width: `${width}%`, background: sector.color }} /></div>
                        <span className="pdf-division-value" style={{ left: `${width}%` }}>{formatCompositionAmount(division.amount)}</span>
                      </div>
                      <span className="pdf-division-value-space" aria-hidden="true" />
                    </div>
                    <span className="pdf-division-count">{division.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PdfCompositionChart;
